import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, DollarSign, Users, Sparkles, ExternalLink, CreditCard } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DonationPage = () => {
  const [customAmount, setCustomAmount] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const presetAmounts = [25, 50, 100, 250, 500, 1000];

  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/payment/public`);
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(data);
      }
    } catch (error) {
      console.error('Failed to load payment settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentIcon = (iconName) => {
    const icons = {
      paypal: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#0070ba">
          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.916c2.077 0 3.573.405 4.44 1.204.855.79 1.188 1.942.989 3.423-.02.152-.044.3-.072.449l-.004.023c-.555 3.01-2.481 4.48-5.719 4.48H9.677a.77.77 0 0 0-.757.65l-.918 5.788a.641.641 0 0 1-.633.74h-.293v1.51z"/>
        </svg>
      ),
      stripe: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#635bff">
          <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
        </svg>
      ),
      default: <CreditCard className="w-5 h-5" />
    };
    return icons[iconName] || icons.default;
  };

  const handleDonate = (e) => {
    e.preventDefault();
    const donationAmount = selectedAmount || customAmount;
    
    if (!donationAmount || donationAmount <= 0) {
      toast({
        title: "Please select an amount",
        description: "Choose a preset amount or enter your own",
        variant: "destructive",
      });
      return;
    }

    const planData = {
      name: 'Donation',
      price: `$${donationAmount}`,
      duration: 'one-time',
      features: [
        'Support qbool mission',
        'Help Muslim families connect',
        'Tax receipt provided',
        'Make a difference'
      ]
    };
    
    // Store in sessionStorage as backup
    sessionStorage.setItem('paymentPlan', JSON.stringify(planData));

    // Navigate to payment page with donation flag
    navigate('/payment', { 
      state: { 
        plan: planData,
        amount: donationAmount,
        type: 'donation',
        isDonation: true
      } 
    });
  };

  // Get donation methods (use separate donation_methods if available, otherwise fall back to payment_methods)
  const donationMethods = paymentSettings?.donation_enabled && paymentSettings?.donation_methods?.length > 0
    ? paymentSettings.donation_methods.filter(m => m.enabled && m.link)
    : [];

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-2 py-12">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-navy-500 mb-6">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Support qbool</h1>
          <p className="text-xl text-gray-600">
            Help us continue our mission of bringing Muslim families together
          </p>
        </div>

        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold mb-6 text-center">Make Your Donation</h2>
            
            <form onSubmit={handleDonate}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Amount (CAD)
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {presetAmounts.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                      className={`py-4 px-6 border-2 rounded-lg font-semibold transition-all ${
                        selectedAmount === amt
                          ? 'border-mint-500 bg-cream-100 text-navy-500'
                          : 'border-gray-300 hover:border-red-400 text-gray-700'
                      }`}
                    >
                      ${amt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or Enter Custom Amount
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                    className="pl-10"
                    min="1"
                  />
                </div>
              </div>

              {(selectedAmount || customAmount) && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-center text-green-800 font-semibold">
                    Your donation: ${selectedAmount || customAmount} CAD
                  </p>
                </div>
              )}

              {/* Show donation payment methods if available */}
              {donationMethods.length > 0 ? (
                <div className="space-y-3 mb-6">
                  <p className="text-sm text-gray-600 text-center mb-4">Choose your donation method:</p>
                  {donationMethods.map((method) => (
                    <a
                      key={method.id}
                      href={method.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 border rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-all"
                    >
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: method.color || '#6b7280' }}
                      >
                        {getPaymentIcon(method.icon)}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold">{method.name}</p>
                        {method.description && (
                          <p className="text-sm text-gray-500">{method.description}</p>
                        )}
                      </div>
                      <ExternalLink className="w-5 h-5 text-gray-400" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white py-6 text-lg"
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Donate Now
                  </Button>
                  <p className="text-center text-sm text-gray-500">
                    Secure payment processing • Tax receipt provided
                  </p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-navy-500 to-pink-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-semibold mb-4">Sadaqah Jariyah</h2>
            <p className="text-lg mb-4">
              Every couple that meets and marries through qbool, you share in the reward. 
              May Allah accept your contribution and bless your wealth.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DonationPage;